/**
 * Tiny DOM helpers. Deliberately not a library: five functions that remove the
 * casting noise from `querySelector` and nothing else.
 */

/** Query within a root, typed, returning null rather than throwing. */
export function qs<T extends Element = HTMLElement>(
  sel: string,
  root: ParentNode = document,
): T | null {
  return root.querySelector<T>(sel);
}

/** Query within a root and throw if absent. For markup this bundle authored. */
export function must<T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T {
  const found = root.querySelector<T>(sel);
  if (!found) throw new Error(`required element not found: ${sel}`);
  return found;
}

export function qsa<T extends Element = HTMLElement>(
  sel: string,
  root: ParentNode = document,
): T[] {
  return Array.from(root.querySelectorAll<T>(sel));
}

/** Create an element with attributes and children in one call. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number | boolean | null> = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === false) continue;
    if (v === true) node.setAttribute(k, '');
    else node.setAttribute(k, String(v));
  }
  for (const child of children) {
    node.append(typeof child === 'string' ? document.createTextNode(child) : child);
  }
  return node;
}

/** addEventListener that returns its own remover. */
export function on<K extends keyof HTMLElementEventMap>(
  target: EventTarget,
  type: K | string,
  handler: (ev: Event) => void,
  options?: AddEventListenerOptions,
): () => void {
  target.addEventListener(type, handler as EventListener, options);
  return () => target.removeEventListener(type, handler as EventListener);
}

/** Replace every child of a node with the given nodes. */
export function replaceChildren(node: Element, ...children: (Node | string)[]): void {
  node.replaceChildren(...children);
}

/**
 * Data attributes by their ATTRIBUTE name, not their `dataset` name.
 *
 * `element.dataset` is the obvious API and it is the wrong one here for two
 * reasons. It renames every attribute on the way through — `data-poll-url`
 * becomes `pollUrl` — so the string in the TypeScript never matches the string
 * in the template, the stylesheet or the test, and a typo in the mapping is
 * silent. And `DOMStringMap` is an index signature, which this repo's
 * `noPropertyAccessFromIndexSignature` forces to bracket syntax that the
 * project's lint rules then reject.
 *
 * One name, spelled the same everywhere, is worth the four lines.
 */
export function data(node: Element, name: string): string | null {
  return node.getAttribute(`data-${name}`);
}

/** Set a data attribute, or remove it when the value is null. */
export function setData(node: Element, name: string, value: string | null): void {
  if (value === null) node.removeAttribute(`data-${name}`);
  else node.setAttribute(`data-${name}`, value);
}
